import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import styles from './Auth.module.css';
import { API_BASE_URL } from '../../services/api';

const Login = ({ onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [smsData, setSmsData] = useState({
    phone: '',
    code: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loginMode, setLoginMode] = useState('password'); // 'password' | 'sms'
  const { login, loginWithSms } = useAuth();

  useEffect(() => {
    if (!countdown) return;
    const timer = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (loginMode === 'password') {
    if (!formData.username.trim()) {
      newErrors.username = '请输入用户名或手机号';
    } else if (formData.username.length < 3) {
      newErrors.username = '用户名至少3个字符';
    }

    if (!formData.password) {
      newErrors.password = '请输入密码';
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6个字符';
      }
    } else if (loginMode === 'sms') {
      if (!smsData.phone.trim()) {
        newErrors.phone = '请输入手机号';
      } else if (!/^\d{6,}$/.test(smsData.phone.trim())) {
        newErrors.phone = '请输入正确的手机号';
      }
      if (!smsData.code.trim()) {
        newErrors.code = '请输入验证码';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let result;
      if (loginMode === 'password') {
        result = await login(formData.username, formData.password);
      } else {
        result = await loginWithSms(smsData.phone.trim(), smsData.code.trim());
      }
      
      if (result.success) {
        console.log('登录成功');
        setErrors({});
      } else {
        setErrors({
          general: '用户名或密码错误，请重试',
        });
      }
    } catch (error) {
      setErrors({
        general: '登录失败，请稍后重试',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSmsChange = (e) => {
    const { name, value } = e.target;
    setSmsData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSendCode = async () => {
    const phone = smsData.phone.trim();
    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: '请输入手机号' }));
      return;
    }
    if (!/^\d{6,}$/.test(phone)) {
      setErrors((prev) => ({ ...prev, phone: '请输入正确的手机号' }));
      return;
    }
    setErrors((prev) => ({ ...prev, general: '', generalDetail: '' }));
    setSending(true);
    try {
      const response = await fetch(`${API_BASE_URL}/send_sms_code/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || '验证码发送失败');
      }
      setCountdown(60);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        general: error.message || '验证码发送失败，请稍后重试',
      }));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      <div className={styles.loginCard}>
        <div className={styles.loginVisual}>
          <div className={styles.visualBadge}>EnNovels</div>
          <h2 className={styles.visualTitle}>欢迎回到阅读的世界</h2>
          <p className={styles.visualSubtitle}>精读 · 听力 · 笔记 · 词库</p>
        </div>

        <div className={styles.loginFormSection}>
          <div className={styles.formHeader}>
            <h1 className={styles.formTitle}>欢迎回来</h1>
            <p className={styles.formSubtitle}>登录您的账户继续阅读</p>
          </div>

          <div className={styles.loginTypeTab}>
            <button
              type="button"
              className={`${styles.tabItem} ${loginMode === 'password' ? styles.tabItemActive : ''}`}
              onClick={() => {
                setLoginMode('password');
                setErrors({});
              }}
              disabled={loading}
            >
              密码登录
            </button>
            <button
              type="button"
              className={`${styles.tabItem} ${loginMode === 'sms' ? styles.tabItemActive : ''}`}
              onClick={() => {
                setLoginMode('sms');
                setErrors({});
              }}
              disabled={loading}
            >
              短信验证码登录
            </button>
          </div>

          <form className={styles.loginForm} onSubmit={handleSubmit}>
            {errors.general && (
              <div className={`${styles.errorMessage} ${styles.generalError}`}>
                {errors.general}
              </div>
            )}

            {loginMode === 'password' && (
              <>
                <div className={styles.inputGroup}>
                  <label htmlFor="username">用户名</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    className={`${styles.inputField} ${errors.username ? styles.inputError : ''}`}
                    placeholder="请输入用户名或手机号"
                    value={formData.username}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.username && <div className={styles.errorMessage}>{errors.username}</div>}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="password">密码</label>
                  <div className={styles.inputWithIcon}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      className={`${styles.inputField} ${styles.inputFieldWithIcon} ${errors.password ? styles.inputError : ''}`}
                      placeholder="请输入密码"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className={styles.eyeIcon}
                      onClick={() => setShowPassword((v) => !v)}
                      disabled={loading}
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                    >
                      <span className="material-symbols-rounded">
                        {showPassword ? 'visibility' : 'visibility_off'}
                      </span>
                    </button>
                  </div>
                  {errors.password && <div className={styles.errorMessage}>{errors.password}</div>}
                </div>
              </>
            )}

            {loginMode === 'sms' && (
              <>
                <div className={styles.inputGroup}>
                  <label htmlFor="phone">手机号</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className={`${styles.inputField} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="请输入手机号"
                    value={smsData.phone}
                    onChange={handleSmsChange}
                    disabled={loading || sending}
                  />
                  {errors.phone && <div className={styles.errorMessage}>{errors.phone}</div>}
                </div>

                <div className={styles.inputGroup}>
                  <label htmlFor="code">短信验证码</label>
                  <div className={styles.codeRow}>
                    <input
                      type="text"
                      id="code"
                      name="code"
                      className={`${styles.inputField} ${errors.code ? styles.inputError : ''}`}
                      placeholder="请输入验证码"
                      value={smsData.code}
                      onChange={handleSmsChange}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className={styles.codeBtn}
                      onClick={handleSendCode}
                      disabled={sending || countdown > 0 || loading}
                    >
                      {countdown > 0 ? `重新发送 (${countdown}s)` : '获取验证码'}
                    </button>
                  </div>
                  {errors.code && <div className={styles.errorMessage}>{errors.code}</div>}
                </div>
              </>
            )}

            <button type="submit" className={styles.loginBtn} disabled={loading}>
              {loading ? '登录中...' : '登录'}
            </button>
          </form>

          <div className={styles.formFooter}>
            <div className={styles.switchText}>
              还没有账户？
              <button
                type="button"
                className={styles.switchBtn}
                onClick={onSwitchToRegister}
                disabled={loading}
              >
                立即注册
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
